import React, { useState } from "react";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import {
  Bars3Icon,
  BellIcon,
  ChatBubbleOvalLeftIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { SettingIcon } from "../components/icons";
import { useLayoutContext } from "../core/context/layout-context";
import { NavigationInterface } from "../core/models";
import { useThemeContext } from "../core/context/theme-context";
import GatewayApiKeysModal from "../components/modals/gateway-api-keys-modal";

interface HeaderProps {
  openAside: boolean;
  setAsideOpen: () => void;
}

export default function Header({ openAside, setAsideOpen }: HeaderProps) {
  const [keysModalOpen, setKeysModalOpen] = useState(false);
  const location = useLocation();
  const { navigations, asideWidth, isMobile, mobileSidebarOpen, setMobileSidebarOpen } = useLayoutContext();
  const { color, handleColor } = useThemeContext();

  const handleToggle = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setAsideOpen();
    }
  };

  return (
    <header
      style={{
        paddingLeft: isMobile ? "16px" : openAside ? `${asideWidth + 16}px` : "96px",
      }}
      className={classNames(
        `${
          color === "green"
            ? "bg-green-900"
            : color === "purple"
            ? "bg-purple-900"
            : color === "red"
            ? "bg-red-900"
            : color === "blue"
            ? "bg-blue-900"
            : color === "brown"
            ? "bg-stone-900"
            : "bg-gray-900"
        }`,
        "transition-[padding-left] duration-200 ease-in-out flex justify-between items-center fixed top-0 h-[50px] border-b border-white/10 w-full px-4 xl:pr-6 z-40 shadow-sm"
      )}
    >
      {!isMobile && (
        <a
          href="/"
          style={{
            left: openAside ? `${Math.max(16, (asideWidth - 40) / 2)}px` : "24px",
          }}
          className="absolute transition-all duration-200 ease-in-out flex items-center h-full"
        >
          <span
            className={classNames(
              "transition-all duration-300 ease-in-out text-white text-xl font-bold tracking-wider"
            )}
          >
            KU
          </span>
        </a>
      )}

      <nav className="h-full flex items-center min-w-0 flex-1 overflow-hidden mr-4">
        <button
          type="button"
          className="p-1.5 text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer mr-3 shrink-0"
          onClick={handleToggle}
          title={isMobile ? "Menu" : "Réduire / agrandir la barre latérale"}
        >
          <span className="sr-only">Toggle navigation</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>

        {isMobile && (
          <a href="/" className="mr-4 shrink-0">
            <span className="text-white text-lg font-bold tracking-wider">
              KU
            </span>
          </a>
        )}

        <ul className="flex items-center gap-x-1 md:gap-x-2 xl:gap-x-5 overflow-x-auto tracking-scrollbar py-1">
          {navigations.map((elmt, i) => {
            const item = elmt as NavigationInterface;
            return (
              <Link
                key={i}
                to={item.href}
                onClick={() => handleColor(item.color)}
              >
                <li
                  className={classNames(
                    {
                      " ": location.pathname === item.href,
                    },
                    {
                      "text-gray-300 font-light":
                        location.pathname !== item.href,
                    },
                    location.pathname === item.href
                      ? `${
                          color === "green"
                            ? "bg-green-100 text-green-700"
                            : color === "purple"
                            ? "bg-purple-100 text-purple-700"
                            : color === "red"
                            ? "bg-red-100 text-red-700"
                            : color === "blue"
                            ? "bg-blue-100 text-blue-700"
                            : color === "brown"
                            ? "bg-stone-100 text-stone-700"
                            : "bg-gray-100 text-gray-700"
                        } font-medium`
                      : "text-gray-300 font-light",
                    `${
                      color === "green"
                        ? "hover:text-green-700"
                        : color === "purple"
                        ? "hover:text-purple-700"
                        : color === "red"
                        ? "hover:text-red-700"
                        : color === "blue"
                        ? "hover:text-blue-700"
                        : color === "brown"
                        ? "hover:text-stone-700"
                        : "hover:text-gray-700"
                    }`,
                    `${
                      color === "green"
                        ? "hover:bg-green-100"
                        : color === "purple"
                        ? "hover:bg-purple-100"
                        : color === "red"
                        ? "hover:bg-red-100"
                        : color === "blue"
                        ? "hover:bg-blue-100"
                        : color === "brown"
                        ? "hover:bg-stone-100"
                        : "hover:bg-gray-100"
                    }`,
                    "text-sm cursor-pointer   flex items-center gap-x-2 px-2 py-[5.5px] rounded-2xl group"
                  )}
                >
                  <item.icon
                    className={classNames(
                      location.pathname === item.href
                        ? `${
                            color === "green"
                              ? "text-green-600"
                              : color === "purple"
                              ? "text-purple-600"
                              : color === "blue"
                              ? "text-blue-600"
                              : color === "red"
                              ? "text-red-600"
                              : color === "blue"
                              ? "text-blue-600"
                              : color === "brown"
                              ? "text-stone-600"
                              : "text-gray-600"
                          }`
                        : "text-gray-300",
                      `${
                        color === "green"
                          ? "group-hover:text-green-600"
                          : color === "purple"
                          ? "group-hover:text-purple-600"
                          : color === "red"
                          ? "group-hover:text-red-600"
                          : color === "blue"
                          ? "group-hover:text-blue-600"
                          : color === "brown"
                          ? "group-hover:text-stone-600"
                          : "group-hover:text-gray-600"
                      }`,
                      "h-4 w-4 shrink-0 "
                    )}
                    aria-hidden="true"
                  />
                  {item.name}{" "}
                </li>
              </Link>
            );
          })}
        </ul>
      </nav>
      <div className="relative flex items-center gap-x-4 lg:gap-x-6 shrink-0">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setKeysModalOpen(true)}
            className="p-1 text-white hover:text-purple-200 transition-colors"
            title="Cles d'API Passerelle (Clementino, Passenger, Yangzi)"
          >
            <KeyIcon className="h-6 w-6 text-white" />
          </button>
          <ChatBubbleOvalLeftIcon className="h-6 w-6 text-white" />
          <BellIcon className="h-6 w-6 text-white" />
          <SettingIcon className="h-6 w-6 text-white" />
        </div>
        <div className="flex flex-row gap-x-2">
          <img
            src="https://randomuser.me/api/portraits/men/20.jpg"
            className="rounded-full"
            width="40"
            alt="Profil"
          />
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="flex flex-row items-start gap-2">
                <span className="text-white text-sm font-medium">Lavern</span>
                <span className="text-white text-sm font-medium">L.</span>
              </div>
              <span className="text-[10px] text-gray-200">Frontend Dev</span>
            </div>
          </div>
        </div>
      </div>
      <GatewayApiKeysModal isOpen={keysModalOpen} onClose={() => setKeysModalOpen(false)} />
    </header>
  );
}
